import { useCallback, useState } from 'react'
import type { Invite } from '../../types'
import { weddingInfo } from '../../weddingInfo'
import { SectionHeading } from '../SectionHeading'
import { GuestDietaryForm } from './GuestDietaryForm'

interface DietarySectionProps {
  invite: Invite
  onSaved: (invite: Invite) => void
  onSessionLost: () => void
}

/**
 * The food part of the invite: one form per guest who is coming.
 *
 * Only appears once somebody has said yes, and only for the people who did -
 * there is nothing to ask a guest who cannot make it, and asking before they
 * have replied puts the questions in the wrong order.
 */
export function DietarySection({ invite, onSaved, onSessionLost }: DietarySectionProps) {
  /** Whose picker is currently open. */
  const [editingIds, setEditingIds] = useState<ReadonlySet<string>>(() => new Set())

  const attending = invite.people.filter((p) => p.response === 'yes')

  const handleEditingChange = useCallback((personId: string, editing: boolean) => {
    setEditingIds((current) => {
      if (current.has(personId) === editing) return current
      const next = new Set(current)
      if (editing) next.add(personId)
      else next.delete(personId)
      return next
    })
  }, [])

  if (attending.length === 0) return null

  // Everything collected, and nobody part way through changing it. Both halves
  // matter: an open picker needs the instruction, however complete the saved
  // answer behind it happens to be.
  const settled = editingIds.size === 0 && attending.every((person) => person.dietary_complete)

  return (
    <section aria-labelledby="food">
      <SectionHeading id="food">Food</SectionHeading>

      {/* Once everyone has answered, the instruction has nothing left to
          instruct, so it steps aside for the reassurance instead. */}
      <p className="mt-3 text-sm">
        {settled
          ? `That is everything - we will pass it on to the kitchen. You can change ${attending.length > 1 ? 'any of it' : 'it'} any time before ${weddingInfo.dietaryBy}.`
          : `Please tell us about any dietary requirements${attending.length > 1 ? ' for each of you' : ''}. This is separate from your reply, and you can come back and change it any time before ${weddingInfo.dietaryBy}.`}
      </p>

      <div className="mt-5 space-y-5">
        {attending.map((person) => (
          <GuestDietaryForm
            // Keyed by person, so switching guests never carries one person's
            // half-made answer into another's form.
            key={person.person_id}
            person={person}
            onSaved={onSaved}
            onEditingChange={handleEditingChange}
            onSessionLost={onSessionLost}
          />
        ))}
      </div>
    </section>
  )
}
